import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import "@/dealer/DealerWorkspace.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Superadmin / Admin app = the former "Test workspaces" surface.
 * - Auto-opens the admin Test Workspace.
 * - Covers the iframe with a branded overlay until the admin surface is open,
 *   so the store/dealer view never flashes for a superadmin.
 * - Shows the REAL signed-in superadmin in the header; "Invite member" → "Create user".
 * - Replaces the fabricated dashboard KPIs with REAL numbers from /api/admin/stats.
 */
export default function AdminWorkspace({ embedded = false }) {
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;
    let cleanup = () => {};

    const onLoad = async () => {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc) return;

      const pin = doc.getElementById("pin-lock-screen");
      if (pin) pin.style.display = "none";

      // Wire the configurator to the server-side Advanced Cabinet Rule Engine.
      try {
        win.LHC_RULE_CONFIG = {
          apiBase: process.env.REACT_APP_BACKEND_URL,
          token: localStorage.getItem("lhc_token") || "",
          projectId: null,
        };
        if (win.LHCRuleBridge && win.LHCRuleBridge.init) win.LHCRuleBridge.init();
      } catch (_) { /* cross-frame guard */ }

      // Platform branding on the Superadmin surface: never show a dealer logo in
      // the global header — keep the platform "LHC Studio" brand instead.
      if (!doc.getElementById("lhc-admin-branding")) {
        const st = doc.createElement("style");
        st.id = "lhc-admin-branding";
        st.textContent = `
          header.header img, .header.header-dark img { display: none !important; }
          header.header .brand-btn, .header.header-dark .brand-btn { display: inline-flex !important; }
          /* "Switch test account" is not a real login method — hide the persona switcher. */
          .acct-switcher, #acct-switcher { display: none !important; }
          #user-menu-wsp { display: none !important; }
        `;
        doc.head.appendChild(st);
      }

      // fetch real platform metrics up-front
      let stats = null;
      try {
        const { data } = await axios.get(`${API}/admin/stats`);
        stats = data;
      } catch (_) { /* keep going; show seeded values */ }

      const signout = (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        logout().finally(() => navigate("/login", { replace: true }));
      };

      const initials = (user?.name || "Superadmin").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
      const platformName = user?.branding_context?.company_name || "LHC Platform";

      const STAT_MAP = {
        "total accounts": "total_users",
        "active dealers": "active_dealers",
        "configurations": "active_configurations",
        "orders (30d)": "orders_this_month",
        "api sync (24h)": "products",        // relabelled to Products (real)
        "system health": "audit_events",     // relabelled to Audit events (real)
      };
      const RELABEL = { "api sync (24h)": "Products", "system health": "Audit events" };

      const patchStats = () => {
        if (!stats) return;
        doc.querySelectorAll(".sa-kpi").forEach((card) => {
          const labelEl = card.querySelector(".sa-kpi-label");
          const numEl = card.querySelector(".sa-kpi-num");
          if (!labelEl || !numEl) return;
          const key = labelEl.textContent.trim().toLowerCase();
          if (STAT_MAP[key] != null && stats[STAT_MAP[key]] != null) {
            numEl.textContent = String(stats[STAT_MAP[key]]);
            if (RELABEL[key]) labelEl.textContent = RELABEL[key];
            const trend = card.querySelector(".sa-kpi-trend");
            if (trend) trend.textContent = "from live data"; // remove fake +% deltas
          }
        });
      };

      const brandIdentity = () => {
        const set = (id, v) => { const el = doc.getElementById(id); if (el && v != null) el.textContent = v; };
        if (user) {
          set("header-user-name", user.name);
          set("header-user-avatar", initials);
          set("user-menu-name", user.name);
          set("user-menu-email", user.email);
          set("user-menu-avatar", initials);
        }
        // The Superadmin is already inside the admin surface — the redundant
        // "Test workspaces" menu item is noise; remove it.
        const wsp = doc.getElementById("user-menu-wsp");
        if (wsp) wsp.style.display = "none";
        // Account settings must show PLATFORM identity, not a dealer company.
        set("acct-company-logo-name", platformName);
        const acctLogo = doc.querySelector("#acct-logo-box img, .acct-company-logo img");
        if (acctLogo) acctLogo.style.display = "none";
        if (!doc.__lhcAcctFilled) {
          const cn = doc.getElementById("acct-company-name");
          const ln = doc.getElementById("acct-legal-name");
          if (cn) { cn.value = platformName; doc.__lhcAcctFilled = true; }
          if (ln) ln.value = platformName;
        }
        // "Invite member" → "Create user" (button + submit)
        const inviteBtn = doc.getElementById("team-invite-toggle");
        if (inviteBtn) {
          const span = inviteBtn.querySelector("span");
          if (span && span.textContent.trim().toLowerCase() === "invite member") span.textContent = "Create user";
        }
        const sendBtn = doc.getElementById("team-invite-send");
        if (sendBtn && sendBtn.textContent.trim().toLowerCase() === "send invitation") sendBtn.textContent = "Create user";
        // Wire the create-user submit to the REAL backend (superadmin controls users).
        if (sendBtn && !sendBtn.__lhcBound) {
          sendBtn.__lhcBound = true;
          sendBtn.addEventListener("click", async (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            const ROLE_MAP = { sales: "SALES", "sales lead": "SALES", designer: "DESIGNER", manager: "STORE_ADMIN", installer: "INSTALLER" };
            const name = (doc.getElementById("team-invite-name") || {}).value || "";
            const email = (doc.getElementById("team-invite-email") || {}).value || "";
            const roleRaw = ((doc.getElementById("team-invite-role") || {}).value || "sales").toLowerCase();
            if (!name.trim() || !email.trim()) { win.alert && win.alert("Full name and work email are required."); return; }
            try {
              await axios.post(`${API}/admin/users`, {
                name: name.trim(), email: email.trim(),
                role: ROLE_MAP[roleRaw] || "SALES",
                password: "Welcome123!",
              });
              win.alert && win.alert("User created in MongoDB.\nEmail: " + email.trim() + "\nTemp password: Welcome123!");
              const form = doc.getElementById("team-invite-form");
              if (form) form.hidden = true;
            } catch (err) {
              const d = err?.response?.data?.detail;
              win.alert && win.alert("Could not create user: " + (typeof d === "string" ? d : "error"));
            }
          }, true);
        }
        patchStats();
      };

      let signoutBound = false;
      let opened = false;
      const setup = () => {
        if (!signoutBound) {
          const btn = doc.getElementById("user-menu-signout");
          if (btn) { btn.addEventListener("click", signout, true); signoutBound = true; }
        }
        if (!opened && win.LHCWorkspaces && typeof win.LHCWorkspaces.open === "function") {
          try { win.LHCWorkspaces.open(); opened = true; setTimeout(() => setReady(true), 250); } catch (_) {}
        }
      };

      setup();
      const interval = setInterval(setup, 150);
      const brand = setInterval(brandIdentity, 400);
      brandIdentity();
      let ticks = 0;
      const stopper = setInterval(() => {
        ticks += 1;
        if ((signoutBound && opened) || ticks > 80) { clearInterval(interval); clearInterval(stopper); if (ticks > 80) setReady(true); }
      }, 150);

      cleanup = () => {
        clearInterval(interval); clearInterval(stopper); clearInterval(brand);
        const btn = doc.getElementById("user-menu-signout");
        if (btn) btn.removeEventListener("click", signout, true);
      };
    };

    iframe.addEventListener("load", onLoad);
    return () => { iframe.removeEventListener("load", onLoad); cleanup(); };
  }, [logout, navigate, user]);

  if (embedded) {
    // Rendered inside the real React AdminApp as the "legacy tools" tab.
    return (
      <div data-testid="admin-workspace-embedded" style={{ position: "relative" }}>
        {!ready && (
          <div data-testid="admin-loading-overlay" style={{
            position: "absolute", inset: 0, zIndex: 5, display: "flex", alignItems: "center", justifyContent: "center",
            background: "#0f1012", color: "#e7e7ea", borderRadius: 12,
          }}>
            <div style={{ fontSize: 14, color: "#8a8a93" }}>Opening prototype workspace…</div>
          </div>
        )}
        {user && <div className="lhc-dealer-srlabel" aria-live="polite">Superadmin — {user.name} ({user.email})</div>}
        <iframe ref={iframeRef} data-testid="admin-iframe" title="LHC Superadmin" src="/lhc-app.html" className="lhc-legacy-frame" />
      </div>
    );
  }

  return (
    <div className="lhc-dealer-shell" data-testid="admin-workspace">
      {!ready && (
        <div
          data-testid="admin-loading-overlay"
          style={{
            position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center",
            background: "#0f1012", color: "#e7e7ea", fontFamily: "Inter, -apple-system, sans-serif",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 12, letterSpacing: ".18em", textTransform: "uppercase", color: "#8a8a93" }}>
              {user?.branding_context?.company_name || "LHC Platform"}
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, marginTop: 8 }}>Opening Superadmin control…</div>
          </div>
        </div>
      )}
      {user && (
        <div className="lhc-dealer-srlabel" aria-live="polite">
          Superadmin — {user.name} ({user.email})
        </div>
      )}
      <iframe
        ref={iframeRef}
        data-testid="admin-iframe"
        title="LHC Superadmin"
        src="/lhc-app.html"
        className="lhc-dealer-iframe"
      />
    </div>
  );
}
